import { Fragment, useEffect, useMemo, useRef } from "react"
import Select from "../../../../../../CommonComponents/select/Select"
import DatetimeInput from "../../../../../../CommonComponents/DatetimeInput"
import { containsSpecialChars, getCharacterLimitMessage, getInvalidRangeErrorMessage, getSpecialCharacterMessage, showToastMessage, validateNumberWithoutDecimal } from "../../../../../../../utils/commonFunctions"
import { CustomScheduleModalDataType, CustomScheduleModalFormType, TaskPlanDetails } from "../../../../../types"
import { InfoSvg, PlusSvg } from "../../../../../../CommonComponents/svgs/Svgs"
import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip"
import TelecommandForm from "./TelecommandForm"
import { calculateTimeBasedOnTimeOption, createScheduleTimeValidator, maxScheduleDuration } from "../../../../../utils"

const SourceOption = [
    { name: 'taskPlan', label: 'Task Plan' },
    { name: 'adhocTask', label: 'Ad hoc Task' }
]

const TimeOptions = [
    { name: 'startTime', label: 'Start Time' },
    { name: 'endTime', label: 'End Time' }
]

interface CustomScheduleFormBodyProps {
    formType: CustomScheduleModalFormType
    formData: any
    onChange: (value: any) => void
    taskPlanDetails: TaskPlanDetails
    remainingDuration: number
    disabledSpecification?: boolean
    onDateTimeValidationChange?: (isValid: boolean) => void
    customScheduleModalData: CustomScheduleModalDataType
}

const CustomScheduleFormBody = ({
    formType,
    formData,
    onChange,
    taskPlanDetails,
    remainingDuration,
    disabledSpecification = false,
    onDateTimeValidationChange,
    customScheduleModalData
}: CustomScheduleFormBodyProps) => {
    const { source, sourceId, startTime, duration, taskData, telecommandData } = formData
    const { taskName, adhocTaskId, time, delay, timeOption, taskEndTime, taskSource, taskStartTime } = taskData

    const isTaskForm = useMemo(() => ['add-task'].includes(formType), [formType])
    const isCommandForm = useMemo(() => ['add-telecommand', 'update-telecommand'].includes(formType), [formType])
    const scheduleType = useMemo(() => customScheduleModalData?.scheduleType, [customScheduleModalData?.scheduleType])
    const initialCurrentTime = useMemo(
        () => Math.floor(Date.now() / 1000),
        [customScheduleModalData?.formData?.startTime]
    );

    const scheduleTimeValidatorRef = useRef(createScheduleTimeValidator());
    const selectedTime = isTaskForm ? time : startTime;

    const selectedTaskId = isTaskForm ? adhocTaskId : sourceId;

    const selectFormLabel = useMemo(() => isTaskForm ? 'Task' : source === 'taskPlan' ? 'Task Plan' : 'Ad hoc Task', [isTaskForm, source])
    const newTask = useMemo(() => isTaskForm ? taskSource === 'customTask' : false, [isTaskForm, taskSource])
    const allAdhocTasks = useMemo(() => taskPlanDetails.allAdhocTasks.map((item) => ({ label: item.taskName, value: item.taskId })), [taskPlanDetails])
    const allTaskPlans = useMemo(() => taskPlanDetails.allTaskPlans.map((item) => ({ label: item.taskPlanName, value: item.taskPlanId })), [taskPlanDetails])
    const taskOptions = useMemo(() => (formType === 'schedule' && source === 'taskPlan') ? allTaskPlans : allAdhocTasks, [source, formType, allTaskPlans, allAdhocTasks])
    const selectedTaskOption = useMemo(() => taskOptions.find(item => item.value === selectedTaskId), [taskOptions, selectedTaskId]);

    useEffect(() => {
        return () => {
            scheduleTimeValidatorRef.current.cancel();
        };
    }, []);

    const onChangeTaskData = (value: any) => {
        onChange({ 'taskData': { ...taskData, ...value } })
    }

    const onChangeDuration = (value: string) => {
        if (Number(value) > 1440) return
        onChange({ 'duration': value === '' ? null : value })
    }

    const onChangeTask = (value: string) => {
        if (value === 'create_new') {
            onChangeTaskData({ 'adhocTaskId': '', 'taskSource': 'customTask' })
        } else {
            if (isTaskForm) {
                onChangeTaskData({ adhocTaskId: value, taskSource: 'adhocTask', taskName: '' })
            } else {
                onChange({ sourceId: value })
            }
        }
    }

    const onChangeTime = (value: number | null) => {
        if (isTaskForm) {
            onChangeTaskData({ 'time': value })
        } else {
            onChange({ 'startTime': value })
        }
    }


    if (isCommandForm) {
        return <TelecommandForm
            formType={formType}
            disabledSpecification={disabledSpecification}
            onChange={onChange}
            telecommandData={telecommandData}
            remainingDuration={remainingDuration}
        />
    }

    return (
        <div className='custom__schedule__modal__form__body'>
            {formType === 'schedule' &&
                <div className="form__field__details">
                    <span className="form__field__details__label">Source</span>
                    <div className='form__toggle__button__container'>
                        {SourceOption.map((item) => {
                            const active = source === item.name ? 'active' : ''
                            return (
                                <button key={item.name} className={`form__toggle__button ${active}`}
                                    onClick={() => {
                                        onChange({ 'source': item.name, 'sourceId': null, duration: item?.name === 'adhocTask' ? 0 : 180 })
                                    }}
                                >
                                    {item.label}
                                </button>
                            )
                        })}
                    </div>
                </div>
            }

            <div className="form__field__details">
                <span className="form__field__details__label">{selectFormLabel}</span>
                <div className="w-60">
                    <Select id={1} placement="bottom" value={selectedTaskOption?.value || null} searchable={true}
                        onSelect={(value) => {
                            onChangeTask(value)
                        }}>
                        <Select.Trigger>
                            <Select.Value
                                value={newTask ? 'Create New' : selectedTaskOption?.label}
                                placeholder={`Select ${selectFormLabel}`}
                            />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Group>
                                {taskOptions.map((item) => (
                                    <Select.Item key={item.value} value={item.value}>{item.label}</Select.Item>
                                ))}

                                {isTaskForm &&
                                    <Fragment>
                                        <Select.GroupDivider />
                                        <Select.Item key="create_new" value="create_new">
                                            <div className="flex items-center">
                                                <PlusSvg />
                                                <span>Create New</span>
                                            </div>
                                        </Select.Item>
                                    </Fragment>
                                }
                            </Select.Group>
                        </Select.Content>
                    </Select>
                </div>
            </div>
            <div className="form__field__details__devider" />

            {(isTaskForm && taskData) &&
                <Fragment>
                    {newTask &&
                        <Fragment>
                            <span className="form__field__title">Task Specification</span>
                            <div className="form__field__details">
                                <span className="form__field__details__label">Task Name</span>
                                <div className='form__field__details__input__container w-60'>
                                    <input type='string'
                                        placeholder='Enter Task Name'
                                        value={taskName}
                                        className='form__input large'
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            const trimmedValue = value.trimStart();

                                            if (containsSpecialChars(trimmedValue)) {
                                                showToastMessage('info', getSpecialCharacterMessage());
                                                return;
                                            }

                                            if (trimmedValue.length > 26) {
                                                showToastMessage('info', getCharacterLimitMessage(26));
                                                return;
                                            }

                                            onChangeTaskData({
                                                taskName: trimmedValue,
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="form__field__details__devider" />
                        </Fragment>
                    }
                    <span className="form__field__title">Scheduling Specifications</span>
                    <div className={`form__field__details ${disabledSpecification ? 'disabled' : ''}`}>
                        <span className="form__field__details__label">
                            Delay
                            <sup className="ms-1">
                                <CustomToolTip placement="top-end" maxWidth="220px" title="Delay is the time between end time of previous command and start time of this command.">
                                    <InfoSvg />
                                </CustomToolTip>
                            </sup>
                        </span>
                        <div className='form__toggle__button__container'>
                            {TimeOptions.map((item) => {
                                const active = timeOption === item.name ? 'active' : ''
                                return (
                                    <button key={item.name} className={`form__toggle__button ${active}`}
                                        onClick={() => {
                                            let newStartTime = 0;

                                            const now = Math.floor(Date.now() / 1000); // current UTC in seconds
                                            newStartTime = calculateTimeBasedOnTimeOption({
                                                timeOption: item.name,
                                                startTime: taskStartTime ?? now,
                                                endTime: taskEndTime ?? now,
                                                value: delay ?? 0
                                            });

                                            onChangeTaskData({
                                                'timeOption': item.name,
                                                'time': newStartTime,
                                                'delay': delay ?? 10
                                            })
                                        }}
                                    >
                                        {item.label}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    <div className={`form__field__details ${disabledSpecification ? 'disabled' : ''}`}>
                        <span className="form__field__details__label" />
                        <div className='form__field__details__input__container w-40'>
                            <input type='number'
                                onKeyDown={validateNumberWithoutDecimal}
                                placeholder='Delay'
                                value={delay}
                                className='form__input'
                                onChange={(event) => {
                                    const value = event.target.value;
                                    if (Number(value) < 0) return;
                                    const num = Number(value);
                                    const maxDelaySecs = maxScheduleDuration * 60;
                                    if (num > maxDelaySecs) {
                                        showToastMessage('info', getInvalidRangeErrorMessage('Delay', 1, maxDelaySecs, 'secs'));
                                        return;
                                    }
                                    let newStartTime = 0;
                                    // Always use the latest values from taskData
                                    newStartTime = calculateTimeBasedOnTimeOption({
                                        timeOption,
                                        startTime: taskStartTime,
                                        endTime: taskEndTime,
                                        value: num ?? 0
                                    });
                                    onChangeTaskData({
                                        'delay': num,
                                        'time': newStartTime
                                    });
                                }}
                            />
                            <div className='input__parameter'>sec</div>
                        </div>
                    </div>
                </Fragment>
            }

            <div className="form__field__details">
                <span className="form__field__details__label">Start Time</span>
                <div className="w-70 form__field__details__input__container">
                    <DatetimeInput
                        onChange={(value) => {
                            onChangeTime(value)
                            if (value === null || value === undefined) {
                                scheduleTimeValidatorRef.current.cancel();
                                scheduleTimeValidatorRef.current.reset();
                                return;
                            }
                            
                                scheduleTimeValidatorRef.current({
                                    scheduleTime: value,
                                    scheduleType,
                                    initialStartTime: customScheduleModalData?.formData?.startTime ?? null,
                                    initialCurrentTime
                                });
                            

                        }}
                        value={selectedTime}
                        key={'scenarioStartTime'}
                        onValidationChange={(isValid) => {
                            onDateTimeValidationChange?.(isValid);
                        }}
                    />
                </div>
            </div>
            {(source !== 'adhocTask' && formType === 'schedule') &&
                <div className="form__field__details">
                    <span className="form__field__details__label">Duration</span>
                    <div className='form__field__details__input__container w-40'>
                        <input type='number'
                            onKeyDown={validateNumberWithoutDecimal}
                            placeholder='Duration'
                            value={duration}
                            className='form__input'
                            onChange={(event) => {
                                const value = event.target.value
                                const num = Number(value)
                                if ((num > maxScheduleDuration)) {
                                    showToastMessage('info', getInvalidRangeErrorMessage('Duration', 1, maxScheduleDuration, 'mins'))
                                    return;
                                }
                                onChangeDuration(value)
                            }}
                        />
                        <div className='input__parameter'>min</div>
                    </div>
                </div>
            }
        </div>
    )
}

export default CustomScheduleFormBody
